import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
  duration: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  public readonly toasts = signal<Toast[]>([]);
  private readonly dismissalTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private nextId = 0;

  showSuccess(message: string, duration = 5000): void {
    this.show({ id: this.nextId++, message, type: 'success', duration });
  }

  showError(message: string, duration = 5000): void {
    this.show({ id: this.nextId++, message, type: 'error', duration });
  }

  pause(toastId: number): void {
    const timer = this.dismissalTimers.get(toastId);
    if (timer === undefined) {
      return;
    }

    clearTimeout(timer);
    this.dismissalTimers.delete(toastId);
  }

  resume(toastId: number): void {
    if (this.dismissalTimers.has(toastId)) {
      return;
    }

    const toast = this.toasts().find(candidate => candidate.id === toastId);
    if (toast) {
      this.scheduleDismissal(toast);
    }
  }

  remove(id: number): void {
    this.pause(id);
    this.toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  private show(toast: Toast): void {
    this.toasts.update(toasts => [toast, ...toasts]);
    this.scheduleDismissal(toast);
  }

  private scheduleDismissal(toast: Toast): void {
    const timer = setTimeout(() => this.remove(toast.id), toast.duration);
    this.dismissalTimers.set(toast.id, timer);
  }
}
